import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Check, X } from 'lucide-react';

/**
 * DateAssignPopover — popover compatto per riassegnare la data di un MIT
 * o la settimana di un'Azione direttamente da un chip/elemento lista.
 *
 * Props:
 *   - kind: 'mit' (date picker) | 'azione' (week picker)
 *   - currentValue: stringa attuale (es. '2026-06-30' o '2026-W26')
 *   - onSave: async (newValue) => void
 *   - children: il trigger (chip date/week renderizzato dal padre)
 *   - side: lato di apertura ('left' default per panel laterale)
 *   - disabled: se true, popover non si apre (es. item già chiuso)
 *
 * Salvataggio è ottimistico lato caller; questo componente solo gestisce
 * stato locale + chiama onSave, poi chiude.
 */
export default function DateAssignPopover({ kind, currentValue, onSave, children, side = 'left', disabled = false, testid }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [draft, setDraft] = useState(currentValue || '');

  // Resetta il draft a ogni open per evitare valore stale fra sessioni
  const handleOpenChange = (v) => {
    if (disabled) return;
    setOpen(v);
    if (v) setDraft(currentValue || '');
  };

  const handleSave = async (val) => {
    if (!val || val === currentValue) {
      setOpen(false);
      return;
    }
    setPending(true);
    try {
      await onSave(val);
      setOpen(false);
    } catch {
      // L'errore viene già notificato dal toast nel parent useCoachChat
    } finally {
      setPending(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side={side}
        align="start"
        className="w-auto p-3"
        data-testid={testid || `date-assign-popover-${kind}`}
      >
        {kind === 'mit' ? (
          <MitDatePicker draft={draft} setDraft={setDraft} onSave={handleSave} onCancel={() => setOpen(false)} pending={pending} />
        ) : (
          <AzioneWeekPicker draft={draft} setDraft={setDraft} onSave={handleSave} onCancel={() => setOpen(false)} pending={pending} />
        )}
      </PopoverContent>
    </Popover>
  );
}

// ── MIT: date picker nativo ─────────────────────────────────────────────
function MitDatePicker({ draft, setDraft, onSave, onCancel, pending }) {
  return (
    <div className="flex flex-col gap-2 min-w-[200px]">
      <label className="font-mono text-[10px] uppercase tracking-wider text-mute">
        Sposta MIT al
      </label>
      <input
        type="date"
        value={draft || ''}
        onChange={(e) => setDraft(e.target.value)}
        className="border border-line px-2 py-1.5 text-sm font-mono"
        autoFocus
        data-testid="mit-date-input"
      />
      <div className="flex gap-1.5 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 text-mute hover:text-ink"
          disabled={pending}
          data-testid="mit-date-cancel"
        >
          <X size={12} className="inline mr-0.5" /> Annulla
        </button>
        <button
          type="button"
          onClick={() => onSave(draft)}
          className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 bg-ink text-canvas hover:bg-ok disabled:opacity-50"
          disabled={pending || !draft}
          data-testid="mit-date-save"
        >
          <Check size={12} className="inline mr-0.5" /> {pending ? '…' : 'Sposta'}
        </button>
      </div>
    </div>
  );
}

// ── Azione: data specifica (opzionale) + lista settimane ─────────────
function AzioneWeekPicker({ draft, setDraft, onSave, pending }) {
  // draft può essere "YYYY-Www" (flottante in settimana) o "YYYY-MM-DD" (giorno specifico)
  const isDateDraft = /^\d{4}-\d{2}-\d{2}$/.test(draft || '');
  const [dateInput, setDateInput] = useState(isDateDraft ? draft : '');
  const weekLabel = isDateDraft ? labelFromDate(draft) : draft;
  const options = useMemo(() => buildWeekOptions(weekLabel), [weekLabel]);

  return (
    <div className="flex flex-col gap-3 min-w-[240px]">
      <div className="flex flex-col gap-1.5">
        <label className="font-mono text-[10px] uppercase tracking-wider text-mute">
          Giorno specifico (opzionale)
        </label>
        <div className="flex gap-1.5">
          <input
            type="date"
            value={dateInput}
            onChange={(e) => setDateInput(e.target.value)}
            className="border border-line px-2 py-1.5 text-sm font-mono flex-1"
            data-testid="azione-data-input"
          />
          <button
            type="button"
            onClick={() => { if (dateInput) { setDraft(dateInput); onSave(dateInput); } }}
            disabled={pending || !dateInput}
            className="font-mono text-[10px] uppercase tracking-wider px-2 py-1.5 bg-ink text-canvas hover:bg-ok disabled:opacity-50"
            data-testid="azione-data-save"
            title="Fissa l'azione in questo giorno specifico"
          >
            <Check size={11} className="inline" />
          </button>
        </div>
        {isDateDraft && (
          <span className="font-mono text-[9px] text-ok tracking-wider">
            Ora fissata al {draft}
          </span>
        )}
      </div>
      <div className="border-t border-linesoft" />
      <div className="flex flex-col gap-1.5">
        <label className="font-mono text-[10px] uppercase tracking-wider text-mute">
          Oppure flottante in settimana
        </label>
        <div className="flex flex-col gap-0.5 max-h-[200px] overflow-y-auto">
          {options.map((opt) => {
            const active = !isDateDraft && opt.label === draft;
            return (
              <button
                key={opt.label}
                type="button"
                onClick={() => { setDraft(opt.label); onSave(opt.label); }}
                disabled={pending}
                className={`text-left font-mono text-xs px-2 py-1.5 border transition-colors disabled:opacity-50 ${
                  active
                    ? 'border-ink bg-linesoft text-ink'
                    : 'border-transparent text-soft hover:border-line hover:bg-tint hover:text-ink'
                }`}
                data-testid={`azione-week-opt-${opt.label}`}
              >
                <span className="tabular-nums">{opt.label.slice(-3)}</span>
                <span className="ml-2 text-mute">{opt.range}</span>
                {opt.isCurrent && <span className="ml-2 text-[10px] text-ok">·oggi</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Helper: converte una data YYYY-MM-DD nel suo label settimana ISO
function labelFromDate(dateStr) {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return isoWeekLabel(mondayOfISOWeek(d));
  } catch {
    return dateStr;
  }
}

// Helper: dato un label "2026-W26", costruisci ±4 settimane attorno.
function buildWeekOptions(currentLabel) {
  const today = new Date();
  // Parse il label corrente per ottenere la settimana base, fallback ad oggi
  let baseMonday = mondayOfISOWeek(today);
  const m = (currentLabel || '').match(/^(\d{4})-W(\d{1,2})$/i);
  if (m) {
    baseMonday = mondayOfISOWeekFromLabel(parseInt(m[1], 10), parseInt(m[2], 10));
  }
  const todayMonday = mondayOfISOWeek(today);
  const opts = [];
  for (let i = -4; i <= 4; i++) {
    const monday = new Date(baseMonday);
    monday.setDate(monday.getDate() + i * 7);
    opts.push({
      label: isoWeekLabel(monday),
      range: formatRange(monday),
      isCurrent: sameDay(monday, todayMonday),
    });
  }
  return opts;
}

function mondayOfISOWeek(d) {
  const day = d.getDay() || 7; // domenica = 0 → 7
  const monday = new Date(d);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(d.getDate() - (day - 1));
  return monday;
}

function mondayOfISOWeekFromLabel(year, week) {
  // ISO week algorithm: 4 gennaio è sempre nella settimana 1 ISO.
  const jan4 = new Date(year, 0, 4);
  const jan4Monday = mondayOfISOWeek(jan4);
  const monday = new Date(jan4Monday);
  monday.setDate(jan4Monday.getDate() + (week - 1) * 7);
  return monday;
}

function isoWeekLabel(monday) {
  // Calcola anno/settimana ISO partendo dal lunedì.
  const thursday = new Date(monday);
  thursday.setDate(monday.getDate() + 3);
  const year = thursday.getFullYear();
  const jan4 = new Date(year, 0, 4);
  const jan4Monday = mondayOfISOWeek(jan4);
  const diffDays = Math.floor((monday - jan4Monday) / (24 * 3600 * 1000));
  const week = Math.floor(diffDays / 7) + 1;
  return `${year}-W${String(week).padStart(2, '0')}`;
}

function formatRange(monday) {
  const sun = new Date(monday);
  sun.setDate(monday.getDate() + 6);
  const dd = (d) => String(d.getDate()).padStart(2, '0');
  const MM = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
  if (monday.getMonth() === sun.getMonth()) {
    return `${dd(monday)}–${dd(sun)} ${MM[monday.getMonth()]}`;
  }
  return `${dd(monday)} ${MM[monday.getMonth()]}–${dd(sun)} ${MM[sun.getMonth()]}`;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
